/**
 * The live browser view, with the "502 Bad Gateway" failure mode designed out.
 *
 * The problem
 * -----------
 * `provision_browser` hands back a VNC URL as soon as CloudTest allocates the
 * session, which can be a second or two before the node pod's VNC server is
 * actually listening. The URL went straight into an `<iframe src>`, nginx
 * proxied to a port nothing was bound to yet, and the iframe rendered nginx's
 * 502 page — permanently, because a cross-document error page fires `onLoad`,
 * not `onError`, and nothing ever reloaded it.
 *
 * It hit the second and later provisions in a session worst. On the first one
 * the panel is still mounting, which usually hides the gap; by the second the
 * panel is already on screen and the src flips over the instant the tool
 * returns.
 *
 * The fix
 * -------
 * The proxy URL is same-origin with the app, so readiness can simply be
 * checked with `fetch` before the iframe is mounted at all. The frame is only
 * rendered once the endpoint answers; until then the user sees that we are
 * waiting, and if it never comes up they get a real message and a retry
 * instead of a bare gateway error.
 *
 * The backend also waits for the port before returning, so in the normal case
 * the first probe here succeeds immediately. This is the belt to that braces:
 * it also covers node restarts and transient proxy failures, which no amount
 * of waiting at provision time can rule out.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

const POLL_INTERVAL_MS = 1500;
const MAX_WAIT_MS = 45_000;

type Status = 'waiting' | 'ready' | 'unavailable';

interface Props {
  url: string;
  title?: string;
}

const LiveViewFrame: React.FC<Props> = ({ url, title = 'Live Browser Stream' }) => {
  const [status, setStatus] = useState<Status>('waiting');
  const [attempt, setAttempt] = useState(0);
  const generation = useRef(0);

  const probe = useCallback(() => {
    // Bump the generation so any in-flight polling loop for a previous URL
    // (or a previous retry) stops touching state.
    generation.current += 1;
    const mine = generation.current;
    setStatus('waiting');
    setAttempt(0);

    if (!url) {
      setStatus('unavailable');
      return;
    }

    const deadline = Date.now() + MAX_WAIT_MS;
    let timer: number | undefined;

    const tick = async () => {
      if (mine !== generation.current) return;
      try {
        const res = await fetch(url, { cache: 'no-store', redirect: 'follow' });
        if (mine !== generation.current) return;
        if (res.ok) {
          setStatus('ready');
          return;
        }
      } catch {
        // Network error while the node is still coming up — keep waiting.
      }
      if (mine !== generation.current) return;
      if (Date.now() >= deadline) {
        setStatus('unavailable');
        return;
      }
      setAttempt((n) => n + 1);
      timer = window.setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [url]);

  useEffect(() => {
    const cleanup = probe();
    return () => {
      generation.current += 1; // cancel the loop on unmount / url change
      if (cleanup) cleanup();
    };
  }, [probe]);

  if (status === 'ready') {
    return (
      <iframe
        // Remount on URL change so a new session never reuses the old document.
        key={url}
        src={url}
        title={title}
        style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
        allow="clipboard-write"
      />
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        bgcolor: 'grey.50',
        px: 3,
        textAlign: 'center',
      }}
    >
      {status === 'waiting' ? (
        <>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Connecting to the live browser view…
          </Typography>
          {attempt > 2 && (
            <Typography variant="caption" color="text.disabled">
              The browser node is still starting up.
            </Typography>
          )}
        </>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary">
            The live view is not reachable.
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ maxWidth: 380 }}>
            The browser session may have ended, or its node stopped responding.
            Tests continue to run either way — only the video feed is affected.
          </Typography>
          <Button size="small" variant="outlined" onClick={probe}>
            Retry
          </Button>
        </>
      )}
    </Box>
  );
};

export default LiveViewFrame;
